import { useEffect, useState, useCallback } from "react";
import { Search, CreditCard, CheckCircle2, XCircle, Clock } from "lucide-react";
import api, { fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState, StatCard } from "../../components/Shared";
import { Input } from "../../components/ui/input";

export default function AdminPayments() {
  const [payments, setPayments] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    api.get("/admin/payments", { params: { q, status } }).then((r) => setPayments(r.data));
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const totals = (payments || []).reduce((acc, p) => {
    if (p.status === "COMPLETED") acc.collected += p.amount;
    if (p.status === "FAILED") acc.failed += 1;
    if (p.status === "PENDING") acc.pending += 1;
    return acc;
  }, { collected: 0, failed: 0, pending: 0 });

  return (
    <div data-testid="admin-payments">
      <PageHeader title="Payments" subtitle="All PhonePe transactions across the platform." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard icon={CheckCircle2} label="Collected" value={fmtINR(totals.collected)} testid="pay-collected" />
        <StatCard icon={XCircle} label="Failed" value={totals.failed} testid="pay-failed" delay={0.05} />
        <StatCard icon={Clock} label="Pending" value={totals.pending} testid="pay-pending" delay={0.1} />
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative max-w-sm flex-1 min-w-52">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="payment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search txn id or partner…" className="pl-10 h-11 rounded-full" />
        </div>
        <div className="flex gap-2">
          {["", "COMPLETED", "PENDING", "FAILED"].map((s) => (
            <button key={s} data-testid={`pay-filter-${s || "all"}`} onClick={() => setStatus(s)}
              className={`px-4 h-11 rounded-full text-sm font-bold border transition-colors ${status === s ? "bg-primary text-primary-foreground border-transparent" : "border-border hover:border-foreground/40"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {!payments ? <LoadingRows /> : payments.length === 0 ? <EmptyState icon={CreditCard} title="No transactions" body="Payment transactions will appear here." /> : (
        <TableShell testid="payments-table" head={["Transaction", "Partner", "Purpose", "Amount", "Gateway", "Date", "Status"]}>
          {payments.map((p) => (
            <TR key={p.id} testid={`payment-row-${p.id}`}>
              <TD><span className="font-mono text-xs font-bold">{p.txn_id}</span></TD>
              <TD className="text-sm">{p.partner_name || "Store customer"}</TD>
              <TD className="capitalize text-sm">{p.purpose}</TD>
              <TD className="font-bold">{fmtINR(p.amount)}</TD>
              <TD className="capitalize text-sm">{p.gateway}</TD>
              <TD className="text-xs text-muted-foreground">{fmtDateTime(p.created_at)}</TD>
              <TD><StatusBadge status={p.status} /></TD>
            </TR>
          ))}
        </TableShell>
      )}
    </div>
  );
}
